import React, { useEffect, useState, useRef } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import { useAuth } from '../../contexts/AuthContext';
import ParentFilter from './ParentFilter';
import ParentFormDialog from '../admissions/ParentFormDialog';
import { parentsAPI } from '../../services/parentService';
import ParentTable from './ParentTable';
import { classesAPI } from '../../services/classService';

const ParentsInfo = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [loading, setLoading] = useState(true);

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 4,
    page: 1,
    sortField: null,
    sortOrder: null
  });

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const initialParentForm = {
    name: '',
    // lastName: '',
    email: '',
    phone: '',
    address: ''
  };
  const [parentForm, setParentForm] = useState(initialParentForm);
  const [showParentForm, setShowParentForm] = useState(false);
  // Dialog states
  const [editingParent, setEditingParent] = useState(null);
  const [parents, setParents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  useEffect(() => {
    loadParents();
  }, [lazyParams, globalFilterValue]);

  const buildQueryParams = () => {
    const params = {
      page: lazyParams.page || Math.floor(lazyParams.first / Math.max(1, lazyParams.rows)) + 1,
      limit: lazyParams.rows || 10,
      search: globalFilterValue || '',
     
    };

    if (lazyParams.sortField) {
      params.sortField = lazyParams.sortField;
      params.sortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
    }

    return params;
  };

  
  const loadParents = async () => {
    setLoading(true);
    try {
            const params = buildQueryParams();
      if (params.page && isNaN(Number(params.page))) params.page = 1;
      if (params.limit && isNaN(Number(params.limit))) params.limit = 10;
        const res = await parentsAPI.getparentsByParams(params);
       if (res?.data?.success) {
        const data = res.data.data || {};
        setParents(data.parents || []);
        setTotalRecords(data.pagination?.totalRows);
      } else {
        setParents([]);
        setTotalRecords(0);
      }
       } catch (err) {
      console.error('Failed to load parents', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load parents' });
    }
    setLoading(false);
  };

  const handleSaveParent = async () => {
    setLoading(true);
    try {
       if (editingParent) {
        await parentsAPI.updateParent(editingParent.id, parentForm);
        toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Parent updated' });
      } else {
        await parentsAPI.createParent(parentForm);
        toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Parent created' });
      }
  setShowParentForm(false);
      setEditingParent(null);
      loadParents();
    } catch (err) {
      console.error('save parent error', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Failed to save parent' });
    } finally {
      setLoading(false);
    }
  };
  const onGlobalFilterChange = (e) => {
    const value = e.target.value || '';
    setGlobalFilterValue(value);
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, filters: { ...prev.filters, global: { value, matchMode: FilterMatchMode.CONTAINS } } }));
  };
  const handleCreateParent = () => {
    setParentForm(initialParentForm);
    setEditingParent(null);
    setShowParentForm(true);
  };
  const onPage = (event) => {
    setLazyParams((prev) => ({ ...prev, first: event.first, rows: event.rows, page: Math.floor(event.first / event.rows) + 1 }));
  };

  const onFilter = (event) => {
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, filters: event.filters }));
  };
  const onRowToggle = async (e) => {
    const newRows = e.data;
    setExpandedRows(newRows);

    // get the newly expanded id(s)
    const keys = Object.keys(newRows || {});
    if (keys.length === 0) return;
    for (const id of keys) {
      const par = parents.find((p) => String(p.id) === String(id));
      if (!par) continue;
      if (Array.isArray(par.name) && par.name.length > 0) continue; // already loaded

      try {
        setLoading(true);
        const res = await parentsAPI.getparentsByParams(par.id);
        const sections = res?.data?.data || [];
        setParents((prev) => prev.map((item) => (String(item.id) === String(par.id) ? { ...item, sections } : item)));
      } catch (err) {
        console.error('Failed to load sections', err);
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load sections' });
      } finally {
        setLoading(false);
      }
    }
  };
  
  const openEditDialog = (parents) => {
    setEditingParent(parents);
    setParentForm({
      id: parents.id || '',
      name: parents.name || '',
      phone: parents.phone || '',
      email: parents.email || '',
      address: parents.address || '',

    });
    setShowParentForm(true);
  };


  const onSort = (event) => {
    setLazyParams(prev => ({ ...prev, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  return (
    <div style={{ height: 'calc(100vh - 64px)' }} className="flex flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 p-6">
      <Toast ref={toast} />
      {/* Ensure checkbox boxes have visible border when unchecked */}
      <style>{`
        .p-checkbox .p-checkbox-box{border:1px solid #cbd5e1;box-shadow:none}
        .p-checkbox .p-checkbox-box.p-highlight{border-color:#3b82f6;background:linear-gradient(#3b82f6,#2563eb)}
        .p-checkbox .p-checkbox-box:focus{box-shadow:0 0 0 4px rgba(59,130,246,0.08)}
        .p-paginator .p-paginator-pages .p-paginator-page.p-highlight {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        .p-paginator .p-paginator-pages .p-paginator-page:not(.p-highlight):hover {
          background: rgba(102, 126, 234, 0.1) !important;
          border-radius: 8px !important;
        }
      `}</style>

      <ParentFilter
        user={user}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={onGlobalFilterChange}
        handleCreateParent={handleCreateParent}
      />


      <ParentFormDialog
        visible={showParentForm}
        onHide={() => setShowParentForm(false)}
        editingParent={editingParent}
        parentForm={parentForm}
        setParentForm={setParentForm}
        handleSaveParent={handleSaveParent}
        loading={loading}
      />
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="pi pi-user text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-gray-900">Loading Parents</span>
              <span className="text-sm text-gray-500 italic">Please wait while we fetch the directory...</span>
            </div>
          </div>
        </div>
      ) : (
        <ParentTable
          parents={parents}
          lazyParams={lazyParams}
          loading={loading}
          user={user}
          totalRecords={totalRecords}
          onPage={onPage}
          onFilter={onFilter}
          expandedRows={expandedRows}
          onRowToggle={onRowToggle}
          openEditDialog={openEditDialog}
          onSort={onSort}
        />
      )}
    </div>
  );
};

export default ParentsInfo;




